import { Video } from '@ixfx/visual';
import * as Camera from './camera.js';
import * as VideoFile from './video-file.js';
/**
 * Frame Processor
 * Simplifies grabbing frames from a camera or video file.
 *
 * First, create:
 * ```js
 * import { FrameProcessor } from 'https://unpkg.com/ixfx/dist/io.js'
 * const fp = new FrameProcessor();
 * ```
 *
 * Then either use the camera or a video file:
 * ```js
 * fp.useCamera(constraints);
 * // or:
 * gp.useVideo(file);
 * ```
 *
 * With `useCamera`, optionally specify {@link Io.Camera.Constraints} to pick which camera and resolution.
 *
 * ```js
 * fp.getFrame(); // Gets the last frame
 * fp.dispose(); // Close down camera/file
 * ```
 *
 * See {@link FrameProcessorOpts} for details on available options.
 */
export class FrameProcessor {
    _source;
    _state;
    _teardownNeeded = false;
    _cameraConstraints;
    _cameraStartResult;
    _videoSourceCapture;
    _videoFile;
    _videoStartResult;
    _showCanvas;
    _showPreview;
    _postCaptureDraw;
    _timer;
    _captureCanvasEl;
    /**
     * Create a new frame processor
     * @param opts
     */
    constructor(opts = {}) {
        this._state = `ready`;
        this._source = ``;
        this._timer = performance.now();
        this._showCanvas = opts.showCanvas ?? false;
        this._showPreview = opts.showPreview ?? false;
        this._cameraConstraints = opts.cameraConstraints ?? undefined;
        this._captureCanvasEl = opts.captureCanvasEl ?? undefined;
        this._postCaptureDraw = opts.postCaptureDraw;
    }
    /**
     * Hides or shows the raw source in the DOM
     * @param enabled Preview enabled
     */
    showPreview(enabled) {
        if (this._state === `disposed`)
            throw new Error(`Disposed`);
        //eslint-disable-next-line functional/no-let
        let el;
        switch (this._source) {
            case `camera`: {
                el = this._cameraStartResult?.videoEl;
                if (el !== undefined)
                    el.style.display = enabled ? `block` : `none`;
                break;
            }
        }
        this._showPreview = enabled;
    }
    /**
     * Shows or hides the Canvas we're capturing to
     * @param enabled
     */
    showCanvas(enabled) {
        if (this._state === `disposed`)
            throw new Error(`Disposed`);
        //eslint-disable-next-line functional/no-let
        let el;
        if (this._source === `camera` || this._source === `video`) {
            el = this._videoSourceCapture?.canvasEl;
            if (el !== undefined)
                el.style.display = enabled ? `block` : `none`;
        }
        else
            throw new Error(`Source not implemented: ${this._source}`);
        this._showCanvas = enabled;
    }
    /**
     * Returns the current capturer instance
     * @returns
     */
    getCapturer() {
        if (this._state === `disposed`)
            throw new Error(`Disposed`);
        if (this._source === `camera` || this._source === `video`) {
            return this._videoSourceCapture;
        }
        throw new Error(`Source kind not supported ${this._source}`);
    }
    /**
     * Grab frames from a video camera source and initialises
     * frame processor.
     *
     * If `constraints` are not specified, it will use the ones
     * provided when creating the class, or defaults.
     *
     * @param constraints Override of constraints when requesting camera access
     */
    async useCamera(constraints) {
        if (this._state === `disposed`)
            throw new Error(`Disposed`);
        this._source = `camera`;
        if (this._teardownNeeded)
            this.teardown();
        if (constraints)
            this._cameraConstraints;
        await this.init();
    }
    async useVideo(file) {
        if (this._state === `disposed`)
            throw new Error(`Disposed`);
        this._source = `video`;
        if (this._teardownNeeded)
            this.teardown();
        this._videoFile = file;
        await this.init();
    }
    /**
     * Initialises camera
     */
    async initCamera() {
        const r = await Camera.start(this._cameraConstraints);
        if (r === undefined)
            throw new Error(`Could not start camera`);
        this._cameraStartResult = r;
        this.postInit(r);
    }
    async initVideo() {
        if (!this._videoFile)
            throw new Error(`Video file not defined`);
        const r = await VideoFile.start(this._videoFile);
        this._videoStartResult = r;
        this.postInit(r);
    }
    async postInit(r) {
        if (this._showPreview)
            r.videoEl.style.display = `block`;
        // Set up manual capturer
        this._videoSourceCapture = Video.manualCapture(r.videoEl, {
            postCaptureDraw: this._postCaptureDraw,
            showCanvas: this._showCanvas,
            canvasEl: this._captureCanvasEl,
        });
        this._teardownNeeded = true;
        this._cameraStartResult = r;
    }
    /**
     * Closes down connections and removes created elements.
     * Once disposed, the frame processor cannot be used
     * @returns
     */
    dispose() {
        if (this._state === `disposed`)
            return;
        this.teardown();
        this._state = `disposed`;
    }
    async init() {
        this._timer = performance.now();
        switch (this._source) {
            case `camera`: {
                await this.initCamera();
                break;
            }
            case `video`: {
                await this.initVideo();
                break;
            }
        }
        this._state = `initialised`;
    }
    teardown() {
        if (!this._teardownNeeded)
            return;
        if (this._source === `camera` || this._source === `video`) {
            this._videoSourceCapture?.dispose();
        }
        switch (this._source) {
            case `camera`: {
                this._cameraStartResult?.dispose();
                break;
            }
            case `video`: {
                this._videoStartResult?.dispose();
                break;
            }
        }
        this._teardownNeeded = false;
    }
    /**
     * Get the last frame
     * @returns
     */
    getFrame() {
        if (this._state === `disposed`)
            throw new Error(`Disposed`);
        switch (this._source) {
            case `camera`: {
                return this.getFrameCamera();
            }
            case `video`: {
                return this.getFrameCamera();
            }
            default: {
                throw new Error(`source type unhandled ${this._source}`);
            }
        }
    }
    /**
     * Get the timestamp of the processor (elapsed time since starting)
     * @returns
     */
    getTimestamp() {
        return performance.now() - this._timer;
    }
    getFrameCamera() {
        return this._videoSourceCapture?.capture();
    }
}
